import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Globe2, Flag, GraduationCap, ShoppingCart, Sparkles, X } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, CountUp } from "@/components/motion";
import { SectionHeading } from "@/components/shared";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { PRICING, REFUND_POLICY, TESTIMONIALS, STATS, ADMIN_FEES } from "@/data/content";
import { cartApi, useCart } from "@/lib/cart";
import CartBar from "@/components/CartBar";

const BANNER_IMG =
  "https://images.pexels.com/photos/14509172/pexels-photo-14509172.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function AdminFeeRow({ fee, index }) {
  const { admin, addAdmin, removeAdmin } = useCart();
  const [extMonths, setExtMonths] = useState(1);
  const [openForm, setOpenForm] = useState(null); // "extension" | "transfer" | null
  const [extInfo, setExtInfo] = useState({ course_name: "", oen: "" });
  const [transfer, setTransfer] = useState({ from_course: "", to_course: "", oen: "" });

  const inCart = fee.cart && admin.some((a) => a.type === fee.cart);
  const price = fee.cart === "extension" ? fee.price * extMonths : fee.price;

  const addExtension = () => {
    if (!extInfo.course_name.trim()) {
      toast.error("Please add the course name for the extension.");
      return;
    }
    addAdmin({ type: "extension", months: extMonths, course_name: extInfo.course_name.trim(), oen: extInfo.oen.trim() });
    setExtInfo({ course_name: "", oen: "" });
    setOpenForm(null);
  };

  const addTransfer = () => {
    if (!transfer.from_course.trim() || !transfer.to_course.trim()) {
      toast.error("Please fill in both the From and To course codes.");
      return;
    }
    addAdmin({
      type: "transfer",
      from_course: transfer.from_course.trim().toUpperCase(),
      to_course: transfer.to_course.trim().toUpperCase(),
      oen: transfer.oen.trim(),
    });
    setTransfer({ from_course: "", to_course: "", oen: "" });
    setOpenForm(null);
  };

  return (
    <div className={`py-5 ${index > 0 ? "border-t border-slate-100" : ""}`} data-testid={`admin-fee-${index}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div>
          <p className="font-display text-sm font-bold text-ink sm:text-base">{fee.name}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{fee.note}</p>
        </div>
        <div className="flex items-center justify-between gap-3 sm:flex-shrink-0 sm:justify-end">
          {fee.cart === "extension" && !inCart && (
            <select
              value={extMonths}
              onChange={(e) => setExtMonths(Number(e.target.value))}
              className="field !w-auto !py-1.5 text-sm"
              data-testid="tuition-extension-months-select"
              aria-label="Number of months"
            >
              <option value="1">1 month</option>
              <option value="2">2 months</option>
              <option value="3">3 months</option>
              <option value="4">4 months</option>
              <option value="5">5 months</option>
              <option value="6">6 months</option>
            </select>
          )}
          <p className="font-mono text-lg font-bold text-brand">${price}</p>
          {fee.cart && (
            inCart ? (
              <button
                type="button"
                onClick={() => removeAdmin(fee.cart)}
                title="Remove from cart"
                data-testid={`tuition-fee-remove-${fee.cart}`}
                className="flex items-center gap-1.5 rounded-full bg-aqualight px-4 py-2 text-xs font-bold text-aqua"
              >
                <Check className="h-3.5 w-3.5" /> In cart
              </button>
            ) : fee.cart === "plar" ? (
              <button
                type="button"
                onClick={() => addAdmin({ type: "plar" })}
                data-testid={`tuition-fee-cart-${fee.cart}`}
                className="btn-ghost !px-4 !py-2 text-xs"
              >
                <ShoppingCart className="h-3.5 w-3.5" /> Add to cart
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setOpenForm((o) => (o === fee.cart ? null : fee.cart))}
                data-testid={`tuition-fee-cart-${fee.cart}`}
                className="btn-ghost !px-4 !py-2 text-xs"
              >
                <ShoppingCart className="h-3.5 w-3.5" /> {openForm === fee.cart ? "Close" : "Add to cart"}
              </button>
            )
          )}
        </div>
      </div>
      {openForm === "extension" && !inCart && (
        <div className="mt-4 grid gap-3 rounded-2xl bg-sand/70 p-4 sm:grid-cols-3">
          <div>
            <label className="field-label">Course name *</label>
            <input value={extInfo.course_name} onChange={(e) => setExtInfo((t) => ({ ...t, course_name: e.target.value }))} className="field" placeholder="e.g. MCV4U — Calculus and Vectors" data-testid="tuition-extension-course-input" />
          </div>
          <div>
            <label className="field-label">Your OEN (optional)</label>
            <input value={extInfo.oen} onChange={(e) => setExtInfo((t) => ({ ...t, oen: e.target.value }))} className="field" placeholder="9 digit OEN number" data-testid="tuition-extension-oen-input" />
          </div>
          <div className="flex items-end">
            <button type="button" onClick={addExtension} data-testid="tuition-extension-confirm-btn" className="btn-accent !py-2 text-xs w-full sm:w-48">
              <ShoppingCart className="h-3.5 w-3.5" /> Add {extMonths} month{extMonths > 1 ? "s" : ""} — ${price}
            </button>
          </div>
        </div>
      )}
      {openForm === "transfer" && !inCart && (
        <div className="mt-4 grid gap-3 rounded-2xl bg-sand/70 p-4 sm:grid-cols-3">
          <div>
            <label className="field-label">From course *</label>
            <input value={transfer.from_course} onChange={(e) => setTransfer((t) => ({ ...t, from_course: e.target.value }))} className="field" placeholder="e.g. FSF1D" data-testid="tuition-transfer-from-input" />
          </div>
          <div>
            <label className="field-label">To course *</label>
            <input value={transfer.to_course} onChange={(e) => setTransfer((t) => ({ ...t, to_course: e.target.value }))} className="field" placeholder="e.g. CHC2D" data-testid="tuition-transfer-to-input" />
          </div>
          <div>
            <label className="field-label">Your OEN (optional)</label>
            <input value={transfer.oen} onChange={(e) => setTransfer((t) => ({ ...t, oen: e.target.value }))} className="field" placeholder="9 digit OEN number" data-testid="tuition-transfer-oen-input" />
          </div>
          <button type="button" onClick={addTransfer} data-testid="tuition-transfer-confirm-btn" className="btn-accent !py-2 text-xs sm:col-span-3 sm:w-44 sm:justify-self-end">
            <ShoppingCart className="h-3.5 w-3.5" /> Add to cart
          </button>
        </div>
      )}
    </div>
  );
}

export default function Tuition() {
  const cards = [
    { icon: Flag, title: "Domestic students", note: "Ontario & Canadian students", price: PRICING.domestic, testid: "tuition-domestic-card" },
    { icon: Globe2, title: "International students", note: "International Students", price: PRICING.international, testid: "tuition-international-card" },
  ];

  return (
    <>
      <PageHero
        eyebrow="Tuition & Fees"
        lines={[<>One flat fee per credit.</>, <span className="font-serif italic font-medium text-brand">Nothing hidden.</span>]}
        desc="Every course includes teacher support, all learning materials, tutoring help and your proctored final exam. The price you see is the price you pay."
        testid="tuition-page-heading"
      />

      <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="tuition-pricing-section">
        <div className="grid gap-5 md:grid-cols-2">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08} className="h-full">
              <div className="card-line flex h-full flex-col p-8 text-center sm:p-10" data-testid={c.testid}>
                <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-ink text-brandlight">
                  <c.icon className="h-5 w-5" />
                </span>
                <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">{c.note}</p>
                <p className="mt-3 font-display text-5xl font-extrabold text-ink">
                  ${c.price}
                  <span className="ml-2 font-sans text-sm font-medium text-slate-500">CAD per credit</span>
                </p>
                <Link to="/admissions" data-testid={`tuition-apply-${i}`} className="btn-accent mt-9 w-full sm:mt-auto">
                  Apply
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-6 flex items-center justify-center gap-2 text-center font-display text-lg font-extrabold text-ink sm:text-xl" data-testid="tuition-scholarship-line">
            <Sparkles className="h-5 w-5 flex-shrink-0 text-brand" />
            Scholarships available for all full-time students or those who need multiple credits, to support your journey
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="card-line mt-5 p-8 sm:p-10" data-testid="tuition-included-panel">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand">Included with every credit</p>
            <ul className="mt-8 grid gap-x-8 gap-y-3 border-t border-slate-100 pt-8 sm:grid-cols-2">
              <li className="flex items-start gap-3 text-sm text-slate-600" data-testid="tuition-fast-track-line">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-aqua" /> Fast track grading and teacher support (24 to 48 hours)
              </li>
              {PRICING.includes.map((inc) => (
                <li key={inc} className="flex items-start gap-3 text-sm text-slate-600">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-aqua" /> {inc}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      <section className="container-x py-20 sm:py-28" data-testid="ossd-fees-table-section">
        <SectionHeading
          eyebrow="Fee Illustration"
          title={<>Total tuition fees <span className="font-serif italic font-medium text-brand">for the OSSD</span></>}
          desc="What the diploma journey looks like at EGOS, depending on where you are joining us from."
        />
        <Reveal className="mt-12">
          <div className="card-line overflow-x-auto bg-white" data-testid="ossd-fees-table">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left font-mono text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-4 font-medium">Student type</th>
                  <th className="px-5 py-4 font-medium">Min. credits from EGOS</th>
                  <th className="px-5 py-4 font-medium">Per credit fees</th>
                  <th className="px-5 py-4 font-medium">Total</th>
                  <th className="px-5 py-4 font-medium">Scholarship</th>
                  <th className="px-5 py-4 font-medium">Total payable</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Ontario based", 3, 490, 1470, 80, 1390],
                  ["Out-of-province (Canada)", 6, 490, 2940, 150, 2790],
                  ["International students", 8, 890, 7120, 1220, 5900],
                ].map((r, i) => (
                  <tr key={r[0]} className={`border-b border-slate-100 last:border-b-0 ${i === 2 ? "bg-brandlight/40" : ""}`} data-testid={`ossd-fees-row-${i}`}>
                    <td className="px-5 py-4 font-display font-bold text-ink">{r[0]}</td>
                    <td className="px-5 py-4 text-slate-600">{r[1]}</td>
                    <td className="px-5 py-4 text-slate-600">${r[2]}</td>
                    <td className="px-5 py-4 text-slate-600">${r[3]}</td>
                    <td className="px-5 py-4 font-semibold text-aqua">-${r[4]}</td>
                    <td className="px-5 py-4 font-mono font-bold text-brand">${r[5]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="mt-4 max-w-4xl space-y-2 text-xs leading-relaxed text-slate-500" data-testid="ossd-fees-note">
            <li className="flex items-start gap-2.5">
              <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand" />
              <span>These figures assume you join EGOS in Grade 12 with transferred credits for your previous studies. International and out-of-province students must complete a PLAR (Prior Learning Assessment and Recognition) evaluation.</span>
            </li>
          </ul>
        </Reveal>
      </section>

      <section className="border-y border-slate-200/70 bg-sand/50 py-20 sm:py-28" data-testid="admin-fees-section">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Administrative Fees"
              title={<>Only when <span className="font-serif italic font-medium text-brand">these apply</span></>}
              desc="A few optional services carry a separate fee. Everything else — teachers, materials, tutoring and your exam — is inside the per-credit tuition."
            />
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="card-line bg-white px-6 sm:px-8" data-testid="admin-fees-list">
                {ADMIN_FEES.map((f, i) => (
                  <AdminFeeRow key={f.name} fee={f} index={i} />
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="refund-policy" className="container-x py-20 sm:py-28 lg:py-32" data-testid="refund-policy-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Refund Policy"
              title={<>Fair, written down, <span className="font-serif italic font-medium text-brand">no fine print</span></>}
              desc="Tuition is paid in full at the time of enrollment — and we do not lock families into fees for courses that are not the right fit. The essentials are below; the full policy travels with every registration email."
            />
            <p className="mt-4 text-xs text-slate-500">
              More details:{" "}
              <Link to="/refund-policy" data-testid="tuition-refund-policy-link" className="font-semibold text-brand underline underline-offset-2 hover:text-ink">
                Refund Policy
              </Link>
            </p>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <Accordion type="single" collapsible className="card-line bg-white px-6" data-testid="refund-policy-accordion">
                {REFUND_POLICY.map((r, i) => (
                  <AccordionItem key={i} value={`refund-${i}`}>
                    <AccordionTrigger className="text-left font-display text-base font-semibold text-ink" data-testid={`refund-policy-item-${i}`}>
                      {r.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-slate-600">{r.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="student-stories" className="container-x scroll-mt-24 py-20 sm:py-28 lg:py-32" data-testid="testimonials-grid-section">
        <SectionHeading
          eyebrow="Student Stories"
          title={<>Proof, in their <span className="font-serif italic font-medium text-brand">own words</span></>}
          desc="Athletes, future nurses, engineers and designers — students who shaped the OSSD around their lives instead of the other way around."
        />
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={(i % 3) * 0.08} className="h-full">
              <figure className="card-line flex h-full flex-col p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`testimonial-card-${i}`}>
                <img src="/logo.webp" alt="Eletech Global Ontario School crest" className="h-10 w-10 rounded-full bg-white" />
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-700">{t.quote}</blockquote>
                <figcaption className="mt-6 flex items-start gap-3 border-t border-slate-100 pt-4">
                  <GraduationCap className="mt-0.5 h-4 w-4 flex-shrink-0 text-aqua" />
                  <div>
                    <p className="font-display text-sm font-bold text-ink">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.path}</p>
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden" data-testid="testimonials-banner">
        <img src={BANNER_IMG} alt="Graduates celebrating" className="h-[420px] w-full object-cover" data-testid="testimonials-banner-image" />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="container-x grid gap-8 text-center sm:grid-cols-2 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <p className="font-mono text-4xl font-bold text-ivory sm:text-5xl" data-testid={`testimonial-stat-${i}`}>
                  <CountUp to={s.value} suffix={s.suffix} />
                </p>
                <p className="mt-2 text-xs text-ivory/70 sm:text-sm">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CartBar />
    </>
  );
}
